import React, { useMemo, ComponentType } from 'react';

import useTranslation from 'ia-react-core/~/hooks/useTranslation';
import { Response } from 'ia-react-core/~/interfaces/Response';
import Loader from '../../loader/Loader';
import InlineErrorMessage from '../InlineErrorMessage';
import ErrorMessage, { ErrorMessageProps } from '../ErrorMessage';
import { StyledTertiaryButton } from './DataErrorMessage.styles';
import TimeFormat from '../../format/TimeFormat';

export interface DataErrorMessageProps<E = {}> extends Omit<ErrorMessageProps, 'error'> {
  /** Error object. Must include a `status` and a `headers` prop. */
  error?: Partial<Response<E>>;
  /** If the request for data is pending */
  pending?: boolean;
  /** Number of attempts of the current request. */
  attempts?: number;
  /** Maximum number of attempts for server errors. */
  maxAttempts?: number;
  /** Text for network errors */
  networkErrorMessage?: string;
  /** Text for server errors */
  serverErrorMessage?: string;
  /** Text for bad requests */
  badRequestMessage?: string | ((error: Partial<Response<E>>) => string);
  /** Text for service unavailable */
  serviceUnavailableMessage?: string | ((error: Partial<Response<E>>) => string);
  /** Text for too many requests error service */
  tooManyRequestsMessage?: string | ((error: Partial<Response<E>>) => string);
  /** Text for the "try again" message */
  tryAgainMessage?: string;
  /** Text of the cancel button */
  cancelButton?: string;
  /** Text of the retry button */
  retryButton?: string;
  /** Component to override the error message. */
  CustomErrorMessage?: ComponentType;
  /** Display the error inline. */
  inline?: boolean;
  /** Callback of the *Retry* button. */
  onRetry: () => void;
}

const DataErrorMessageDefaultProps = {
  maxAttempts: 3,
  inline: true,
};

const DataErrorMessage = <E = {}>({
  error, pending, networkErrorMessage, badRequestMessage, serverErrorMessage, tryAgainMessage, retryButton, attempts, maxAttempts, onRetry, inline, vertical, serviceUnavailableMessage, tooManyRequestsMessage, ...props
}: DataErrorMessageProps<E>) => {
  const { t } = useTranslation('Core.DataErrorMessage');
  const CustomErrorMessage = useMemo(() => (inline && !vertical) ? InlineErrorMessage : ErrorMessage, [inline, vertical]);
  const headers = useMemo(() => error.headers, [error.headers]);

  const errorMessage = useMemo(() => {
    if (error.status === 0) {
      return networkErrorMessage || t('networkError');
    }
    if (error.status === 429) {
      if (typeof tooManyRequestsMessage === 'function') return tooManyRequestsMessage(error);
      const retryAfter = Date.parse(headers['retry-after']);
      return tooManyRequestsMessage ||
       (Number.isNaN(retryAfter) ?
         t('tooManyRequestError') :
         <>
           {t('tooManyRequestErrorWithTime')} <TimeFormat time={new Date(retryAfter)} isDateWithTime isBreaking={false} />
         </>
       );
    }

    if (error.status >= 400 && error.status < 500) {
      if (typeof badRequestMessage === 'function') return badRequestMessage(error);
      return badRequestMessage || t('badRequest');
    }

    if (error.status === 503) {
      if (typeof serviceUnavailableMessage === 'function') return serviceUnavailableMessage(error);
      const retryAfter = Date.parse(headers['retry-after']);
      return serviceUnavailableMessage ||
      (Number.isNaN(retryAfter) ?
        t('serviceUnavailable') :
        <>
          {t('serviceUnavailableWithTime')} <TimeFormat time={new Date(retryAfter)} isDateWithTime isBreaking={false} />
        </>
      );
    }

    return serverErrorMessage || t('serverError');
  }, [error, serverErrorMessage, t, networkErrorMessage, tooManyRequestsMessage, headers, badRequestMessage, serviceUnavailableMessage]);

  const errorTitle = useMemo(() => {
    if (error.status === 0) {
      return t('connectionErrorTitle');
    }

    if (error.status === 503 || error.status === 429) {
      return t('sorry');
    }

    return t('technicalErrorTitle');
  }, [error.status, t]);

  const isRetryable = useMemo(() => (
    (error.status === 0) ||
    (error.status >= 500)
  ), [error.status]);

  const errorAction = useMemo(() => {
    if (isRetryable) {
      if (error.status >= 500 && attempts > maxAttempts) {
        return tryAgainMessage || t('tryAgain');
      }

      return (
        <CustomErrorMessage.Action>
          <StyledTertiaryButton
            busy={pending}
            inline={inline}
            data-testid="DataErrorMessageRetryButton"
            onClick={onRetry}
          >
            {retryButton || t('retryButton')}
          </StyledTertiaryButton>
        </CustomErrorMessage.Action>
      );
    }

    if (pending) {
      return (
        <CustomErrorMessage.Action>
          <Loader size={24} />
        </CustomErrorMessage.Action>
      );
    }

    return null;
  }, [CustomErrorMessage, attempts, error.status, inline, isRetryable, maxAttempts, onRetry, pending, retryButton, t, tryAgainMessage]);

  return (
    <CustomErrorMessage
      messageTitle={errorTitle}
      vertical={vertical}
      {...props}
    >
      <CustomErrorMessage.Content>
        {errorMessage}
      </CustomErrorMessage.Content>
      {errorAction}
    </CustomErrorMessage>
  );
};

DataErrorMessage.defaultProps = DataErrorMessageDefaultProps;

export default DataErrorMessage;
